import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import {
  type ChangeEvent as ReactChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { type AssetSchema } from 'stores/Asset';
import type Asset from 'stores/Asset';
import store from 'stores/RootStore';
import { AssetFileType } from 'types/AssetFileType';
import { formatDateTimeLongMinutes } from 'utils/Dates';

import FileRowButtons from './FileRowButtons';

const Container = styled.div`
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  justify-content: space-between;
  padding: 9px 10px;
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }
`;

const FileSizeLabel = styled.span``;

const InfoContainer = styled.div`
  align-items: center;
  display: flex;
  font-size: 12px;
  gap: 9px;
  flex-grow: 1;
  margin-right: 10px;
`;

// Styled component for displaying the name and date
const NameAndDateContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const LoadingIcon = styled(LoadingOutlined)`
  width: 24px;
`;

const CheckIcon = styled(CheckOutlined)`
  width: 24px;
`;

const VersionIconContainer = styled.div`
  width: 24px;
`;

const ActionsContainer = styled.div`
  display: flex;
`;

interface LoadableProps {
  $isLoading: boolean;
}

const Label = styled.span<LoadableProps>`
  font-weight: bold;
  font-size: 12px;
  flex-grow: 1;
  opacity: ${(p) => (p.$isLoading ? 0.5 : 1)};
  cursor: pointer;

  /* Handling text overflow */
  max-width: 235px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 5px 5px 5px 0;
`;

// Styled component for the date label
const DateLabel = styled.span`
  font-size: 10px;
  color: #888888;
  margin-top: 2px;
`;

const StyledInput = styled.input<LoadableProps>`
  font-weight: bold;
  opacity: ${(p) => (p.$isLoading ? 0.5 : 1)};
  border: 1px solid #dfdfdf;
  padding: 4px;
  margin: 0;
  border-radius: 4px;
  outline: none;
  font-size: 12px;
  width: 235px;
`;

function extractFilenameWithoutExtension(filename: string): string {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) return filename;
  return filename.substring(0, lastDotIndex);
}

function deriveAssetDataDefaultName(assetData: AssetSchema) {
  if (assetData.name) {
    if (assetData.name === assetData.path) {
      return extractFilenameWithoutExtension(assetData.name);
    }
    return assetData.name;
  }
  return formatDateTimeLongMinutes(assetData.dateModified);
}

export interface VersionFileRowProps {
  asset: Asset;
}

function VersionFileRow({ asset }: VersionFileRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  const { type } = asset.data;
  const { loading: isLoading, fileSizeString } = asset;
  const selected = asset.id === store.animation?.currentAnimationAsset?.id;

  const displayName = deriveAssetDataDefaultName(asset.data);

  const stopEditing = () => {
    setIsEditing(false);
    setEditName('');
  };

  const saveName = async () => {
    const newName = editName.trim();

    if (editName === displayName || newName === displayName) {
      // Do not save the name if it hasn't changed
      return;
    }

    if (newName) {
      await asset.updateName(newName);
      return;
    }

    // If name is empty, revert to default if it's matching path
    if (asset.data.name === asset.data.path) {
      await asset.updateName(displayName);
    }
  };

  const handleLabelClick = () => {
    setIsEditing(true);
    setEditName(displayName);
  };

  const handleInputChange = (e: ReactChangeEvent<HTMLInputElement>) => {
    setEditName(e.target.value);
  };

  const handleInputBlur = () => {
    void saveName();
    stopEditing();
  };

  const handleInputKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        void saveName();
        stopEditing();
        break;

      case 'Escape':
        stopEditing();
        break;
    }
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  return (
    <Container>
      <InfoContainer>
        <VersionIconContainer>
          {isLoading ? <LoadingIcon /> : selected ? <CheckIcon /> : null}
        </VersionIconContainer>
        <NameAndDateContainer>
          {isEditing ? (
            <StyledInput
              type="text"
              value={editName}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              $isLoading={isLoading}
              ref={inputRef}
            />
          ) : (
            <Label $isLoading={isLoading} onClick={handleLabelClick}>
              {displayName}
            </Label>
          )}
          {/* Always display the date below the filename, regardless of editing state */}
          <DateLabel>
            {formatDateTimeLongMinutes(asset.data.dateModified)}
          </DateLabel>
        </NameAndDateContainer>
        <FileSizeLabel>{fileSizeString}</FileSizeLabel>
      </InfoContainer>

      <ActionsContainer>
        {type === AssetFileType.FILE && (
          <FileRowButtons selected={selected} asset={asset} />
        )}
      </ActionsContainer>
    </Container>
  );
}

export default observer(VersionFileRow);
